import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Link,
  alpha,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { authService } from '../../services/authService';
import { apiService } from '../../services/api';
import { API_ENDPOINTS, STORAGE_KEYS } from '../../constants';

const darkFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.06)',
    borderRadius: 2,
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0EA5E9' },
  '& .MuiInputBase-input': { color: 'white', letterSpacing: 4, textAlign: 'center', fontWeight: 700, fontSize: '1.25rem' },
};

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser, logout, isAuthenticated } = useAuth();
  const { showError, showSuccess } = useNotification();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (user?.isEmailVerified) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, user?.isEmailVerified, navigate]);

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = code.trim();
    if (!token) {
      showError('Enter the verification code from your email.');
      return;
    }
    setBusy(true);
    try {
      const res = await authService.verifyEmail(token);
      if (!res.success) {
        throw new Error(res.message || (res as any).error || 'Invalid or expired code');
      }
      const next = user
        ? { ...user, isEmailVerified: true }
        : user;
      if (next) {
        setUser(next);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(next));
      }
      showSuccess('Email verified. Welcome!');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      showError(err?.message || 'Could not verify email. Tap Resend and try again.');
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    setBusy(true);
    try {
      const res = await apiService.post<{ message: string }>(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {});
      if (!res.success) {
        throw new Error(res.message || (res as any).error || 'Could not resend code');
      }
      showSuccess('New code sent. Check inbox and spam.');
    } catch (err: any) {
      showError(err?.message || 'Could not resend code. Please wait a minute and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#0F172A',
        px: 2,
        backgroundImage:
          'radial-gradient(ellipse at top, rgba(14,165,233,0.18), transparent 55%), linear-gradient(180deg, #0F172A 0%, #030B1B 100%)',
      }}
    >
      <Box
        component="form"
        onSubmit={onVerify}
        sx={{
          width: '100%',
          maxWidth: 420,
          p: 4,
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.1)',
          bgcolor: alpha('#0F172A', 0.85),
          backdropFilter: 'blur(12px)',
        }}
      >
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
          Verify your email
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.65)', mb: 3, lineHeight: 1.6 }}>
          We sent a 6-digit code to {user?.email || 'your email'}. Enter it below to activate your
          account. Temporary or fake emails are not allowed.
        </Typography>

        <TextField
          fullWidth
          label="Verification code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
          sx={{ ...darkFieldSx, mb: 2 }}
          inputProps={{ inputMode: 'numeric', maxLength: 8 }}
          autoFocus
        />

        <Button
          type="submit"
          fullWidth
          disabled={busy}
          sx={{
            py: 1.3,
            mb: 1.5,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
            color: 'white',
          }}
        >
          {busy ? <CircularProgress size={22} color="inherit" /> : 'Verify & continue'}
        </Button>

        <Button
          fullWidth
          variant="text"
          disabled={busy}
          onClick={onResend}
          sx={{ color: '#38bdf8', textTransform: 'none', mb: 2 }}
        >
          Resend code
        </Button>

        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
          Wrong account?{' '}
          <Link
            component="button"
            type="button"
            onClick={() => {
              logout();
              navigate('/login');
            }}
            sx={{ color: '#94a3b8' }}
          >
            Sign out
          </Link>
          {' · '}
          <Link component={RouterLink} to="/terms" sx={{ color: '#94a3b8' }}>
            Terms
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default VerifyEmailPage;
